import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import client, { resolveApiAssetUrl } from '../lib/api';
import useSwipe from '../hooks/useSwipe';
import { cacheSliders, readCachedSliders } from '../lib/sliderCache';
import { useSettings } from '../context/SettingsContext';
import { useActiveTheme } from '../lib/activeTheme';
import { pageSliderEnabled, pageSliderSlides, pageSliderUsesMain } from '../lib/pageSlider';
import { HOOKS, hook } from '../lib/domHooks';

/* Katalog sayfalarının (Slotlar, Canlı Casino) üstündeki banner slider'ı.
 *
 * Ana sayfanın `HeroSlider`'ından ayrıdır ve bilinçli olarak daha sadedir:
 * orada banner'ların arasına "Popüler Slot Oyunları" slaytı karışır, burada
 * yalnızca panelden yüklenen banner'lar vardır. Ortak olan tek şey kaynaktır —
 * ikisi de `/sliders` yanıtını okur ve `placement` ile ayrışır, yani yeni bir
 * sayfaya slider eklemek yalnızca yeni bir `placement` değeri demektir.
 *
 * **Banner yoksa hiçbir şey çizilmez** (iskelet de yok): kurulumların çoğunda
 * katalog banner'ı tanımlı değildir ve her açılışta boş bir yer tutucu
 * yanıp sönerdi. Boş dönmek sayfanın kaymasına da yol açmaz, çünkü slider
 * kataloğun üstündedir.
 *
 * Vega şemasında sayfa sayfa açılıp kapatılabilir; yönetici ayrıca isterse
 * o konumda banner yokken ana sayfa banner'larına düşülür (bkz.
 * lib/pageSlider.js). Karar bileşende değil orada durur, çünkü panel de aynı
 * kümeyi çizer.
 */

// Otomatik geçiş; ana slider ile aynı ritim (HeroSlider).
const AUTOPLAY_MS = 5500;

// Gövdede gerçekten çizilen bir şey var mı: karartma buna bağlıdır. Alan
// kümesi aşağıdaki gövde markup'ıyla aynı kalmalıdır — ayrışırsa katman ya
// boş banner'ı karartır ya da metni okunmaz bırakır.
export const slideHasBody = (s) => Boolean(
  s.title || s.subtitle || s.highlight || s.description || s.cta_link,
);

const PageSlider = ({ placement }) => {
  // İlk boyamada önbellekten çizilir (bkz. lib/sliderCache.js); taze liste
  // geldiğinde üzerine yazılır.
  const [slides, setSlides] = useState(readCachedSliders);
  const [loaded, setLoaded] = useState(false);
  const [idx, setIdx] = useState(0);
  const settings = useSettings();
  const themeId = useActiveTheme();
  const enabled = pageSliderEnabled(settings, themeId, placement);
  const useMain = pageSliderUsesMain(settings, themeId, placement);

  useEffect(() => {
    // Kapalı sayfada banner listesi hiç istenmez; slider zaten çizilmeyecek.
    if (!enabled) {
      setLoaded(true);
      return undefined;
    }
    let active = true;
    client.get('/sliders')
      .then(({ data }) => {
        const items = data.items || [];
        cacheSliders(items);
        if (active) setSlides(items);
      })
      .catch(() => {
        // Ağ hatasında önbellekten çizilenler korunur; boşaltmak sayfanın
        // üstünü sebepsiz karartırdı.
      })
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [enabled]);

  const visible = useMemo(
    () => (enabled ? pageSliderSlides(slides, placement, useMain) : []),
    [slides, placement, enabled, useMain],
  );

  // Sayaç idx'e de bağlıdır: parmakla kaydıran oyuncu seçtiği banner'ı tam
  // süre görsün, otomatik geçiş kaydırmanın hemen ardına düşmesin.
  useEffect(() => {
    if (visible.length < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % visible.length), AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [visible.length, idx]);

  useEffect(() => {
    if (idx >= visible.length) setIdx(0);
  }, [idx, visible.length]);

  const swipe = useSwipe({
    enabled: visible.length > 1,
    onNext: () => setIdx((i) => (i + 1) % visible.length),
    onPrev: () => setIdx((i) => (i - 1 + visible.length) % visible.length),
  });

  if (!loaded || visible.length === 0) return null;

  return (
    <div {...hook(HOOKS.SLIDER)} className="page-slider hero-slider" {...swipe}>
      {visible.map((s, i) => (
        <div
          key={s.id}
          className="page-slider-slide"
          style={{ opacity: i === idx ? 1 : 0, pointerEvents: i === idx ? 'auto' : 'none' }}
          aria-hidden={i === idx ? undefined : 'true'}
        >
          {s.image && (
            <img
              src={resolveApiAssetUrl(s.image)}
              alt={s.subtitle || s.title || ''}
              className="page-slider-image"
              loading={i === 0 ? undefined : 'lazy'}
            />
          )}
          {/* Karartma metnin okunması içindir, süs değil: görselden ibaret
              banner'da (panelde başlık/açıklama/CTA boş bırakılmış) katman
              yöneticinin yüklediği tasarımı boşuna karartırdı. */}
          {slideHasBody(s) && <div className="page-slider-veil" />}
          <div className="page-slider-body">
            {s.title && <div className="page-slider-title gold-text">{s.title}</div>}
            {s.subtitle && <div className="page-slider-subtitle">{s.subtitle}</div>}
            {s.highlight && <div className="page-slider-highlight gold-text">{s.highlight}</div>}
            {s.description && <p className="page-slider-desc">{s.description}</p>}
            {s.cta_link && (
              <Link to={s.cta_link} className="gold-btn page-slider-cta">
                {s.cta_text || 'Detaylar'}
              </Link>
            )}
          </div>
        </div>
      ))}

      {visible.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => setIdx((i) => (i - 1 + visible.length) % visible.length)}
            className="hidden sm:flex side-icon-btn page-slider-arrow page-slider-arrow--prev"
            aria-label="Önceki banner"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => setIdx((i) => (i + 1) % visible.length)}
            className="hidden sm:flex side-icon-btn page-slider-arrow page-slider-arrow--next"
            aria-label="Sonraki banner"
          >
            <ChevronRight size={18} />
          </button>
          {/* Referansta sayaç banner'ın sağ üst köşesindedir ve kaç banner
              olduğunu da söyler. Dar ekranda oklar gizlendiği için kaçıncı
              banner'da olunduğunu anlatan tek işaret odur — noktalar uzun
              listede sayılamaz. */}
          <div className="page-slider-count" aria-hidden="true">
            {idx + 1} / {visible.length}
          </div>
          <div className="page-slider-dots">
            {visible.map((s, i) => (
              <button
                key={s.id}
                type="button"
                className={`hero-dot ${i === idx ? 'active' : ''}`}
                onClick={() => setIdx(i)}
                aria-label={`${i + 1}. banner`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PageSlider;
